import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { FieldArray } from 'formik';
import { Formik } from 'components/form/formik';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';

import { PageHeader } from '../../../components/PageHeader';
import { invariant, nullthrows } from '../../../utils/invariant';
import { Country, QuotationType } from 'src/types/enums';
import { useQuotation, generateQuotationPdf, sendQuotationEmail, deleteQuotation, updateQuotation } from '../models';
import { runPdfJobAndRefresh } from '../../../utils/pdf-job-client';
import { decimateAndFormatNumber, parseNumberInput } from '../../../utils/number';
import { DataField } from '../../../components/DataField';
import { formatDate, formatInputDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { FileUploaderButton } from '../../document/components/FileUploaderButton';
import { ContactsDialog } from '../../../components/dialog/ContactsDialog';
import { getDisplayError } from '../../../utils/get-display-error';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { SimpleComboboxField } from '../../../components/combobox/SimpleComboboxField';
import { InputField } from '../../../components/input/InputField';
import { TextAreaField } from '../../../components/textarea/TextAreaField';
import { Button } from '../../../components/button/Button';
import { TrailerTypes } from '../../order/pages/order/TrailerTypes';
import { ILineState, IQuotationLineValues, initialLineValues } from './types';
import { QUOTATION_TYPE_OPTIONS } from '../constants';
import { createRoute } from '../../../utils/mapbox-api.client';
import { serializeCoordinates } from '../../../utils/mapbox-api';
import { ISimpleSelectItem } from '../../../components/select/SimpleSelect';
import { COUNTRY_VALUES } from '../../../utils/address';
import { AutocompletePostalcode } from '../../location/components/AutocompletePostalcode';
import { PageHeading } from 'components/PageHeading';
import { Card } from 'components/Card';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../utils/track-event';
import { DelayedSpinner } from 'components/Spinner';

const createQuotationLineSchema = Yup.object().shape({
  departurePostalCode: Yup.mixed().nullable().required('Vereist'),
  arrivalPostalCode: Yup.mixed().nullable().required('Vereist'),
  distance: Yup.string().required('Vereist'),
  price: Yup.string().required('Vereist'),
  trailerTypes: Yup.array().required('Vereist'),
});

const createQuotationSchema = Yup.object().shape({
  reference: Yup.string().required('Vereist'),
  notes: Yup.string(),
  quotationDate: Yup.string().required('Vereist'),
  expiresAt: Yup.string().required('Vereist'),
  lines: Yup.array(createQuotationLineSchema).required('Vereist'),
});

interface IQuotationValues {
  type: ISimpleSelectItem;
  reference: string;
  quotationDate: string;
  expiresAt: string;
  notes: string;
  lines: IQuotationLineValues[];
}

export const QuotationPage = () => {
  const { quotationId } = useParams<{ quotationId: string }>();
  invariant(quotationId);
  const { isLoading } = useQuotation(quotationId);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  return <QuotationDetail quotationId={quotationId} />;
};

const QuotationDetail: React.FC<{ quotationId: string }> = ({ quotationId }) => {
  const navigate = useNavigate();
  // Cache-shared with the parent's useQuotation call above, so data is already
  // resolved by the time this component mounts.
  const { data, mutate } = useQuotation(quotationId);

  const quotation = nullthrows(data, 'Quotation not found');
  const title = `Offerte ${quotation.reference}`;
  const totalAmount = quotation.lines.reduce((acc, line) => acc + line.price, 0);

  const initialValues: IQuotationValues = useMemo(() => {
    return {
      type: QUOTATION_TYPE_OPTIONS.find((v) => v.key === quotation.type)!,
      reference: quotation.reference,
      quotationDate: formatInputDate(quotation.quotationDate),
      expiresAt: formatInputDate(quotation.expiresAt),
      notes: quotation.notes,
      lines: quotation.lines.map((l) => {
        return {
          departurePostalCode: l.departurePostalCode,
          departureCountry: COUNTRY_VALUES.find((v) => v.key === l.departureCountry)!,
          departureCity: l.departureCity,
          arrivalPostalCode: l.arrivalPostalCode,
          arrivalCountry: COUNTRY_VALUES.find((v) => v.key === l.arrivalCountry)!,
          arrivalCity: l.arrivalCity,
          distance: decimateAndFormatNumber(l.distance, 2),
          price: decimateAndFormatNumber(l.price, 2),
          trailerTypes: l.trailerTypes,
        };
      }),
    };
  }, [quotation]);

  const [_lines, setLines] = useState<ILineState[]>(() => {
    return quotation.lines.map((v) => {
      return {
        departure: undefined,
        arrival: undefined,
        fetchKey: Date.now().toString(),
      };
    });
  });

  return (
    <>
      <PageHeader title={title} />

      <PageHeading
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Offertes',
              to: '/internal/finance/quotations',
            }}
            currentItem={title}
          />
        }
        rightSide={
          <div className="flex gap-2">
            <FileUploaderButton
              buttonText="OFR"
              title="Offerte"
              file={quotation.document}
              generateDocument={async () => {
                await runPdfJobAndRefresh(await generateQuotationPdf(quotationId), async () => {
                  await mutate();
                });
                trackEvent('quotation_pdf_generated', { quotation_id: quotationId });
              }}
              disableUpload
            />

            <ContactsDialog
              triggerText="Verstuur offerte"
              triggerColor="primary"
              title="Verstuur offerte"
              submitText="Verstuur"
              description="Ben je zeker dat je de offerte wilt versturen?"
              contacts={
                quotation.customer?.contacts.map((c) => {
                  return {
                    id: '' + c.id,
                    name: c.name,
                    email: c.email,
                  };
                }) ?? []
              }
              initialValue={
                quotation.customer?.contacts.filter((c) => c.shouldReceiveQuotations).map((v) => '' + v.id) ?? []
              }
              onSubmit={async (contacts) => {
                try {
                  await sendQuotationEmail(
                    quotation.id,
                    contacts.map((c) => +c),
                  );
                  trackEvent('quotation_sent', {
                    quotation_id: quotation.id,
                    contact_count: contacts.length,
                  });
                  toast.success('Offerte verstuurd');
                } catch (err: any) {
                  captureException(err);
                  toast.error('Kon offerte niet versturen: ' + getDisplayError(err));
                  throw err;
                }
              }}
            />

            <ConfirmDialog
              triggerColor="danger"
              triggerText={<TrashIcon className="button-icon" />}
              title="Offerte verwijderen?"
              submitText="Verwijder offerte"
              description={<div>Ben je zeker dat je de offerte wilt verwijderen?</div>}
              onSubmit={async () => {
                try {
                  await deleteQuotation(quotation.id);
                  trackEvent('quotation_deleted', { quotation_id: quotation.id });
                  navigate('..');
                  toast.success('Offerte verwijderd');
                } catch (err) {
                  captureException(err);
                  console.error(err);
                  toast.error('Kon offerte niet verwijderen: ' + getDisplayError(err));
                }
              }}
            />
          </div>
        }
      />

      <div className="px-4">
        <div className="flex gap-4 mb-8">
          <DataField title="Klant">{quotation.customer.name}</DataField>
          <DataField title="Type">{quotation.type}</DataField>
          <DataField title="Referentie">{quotation.reference}</DataField>
          <DataField title="Offerte Datum">{formatDate(quotation.quotationDate)}</DataField>
          <DataField title="Vervalt op">{formatDate(quotation.expiresAt)}</DataField>
          <DataField title="Aangemaakt op">{formatDate(quotation.createdAt)}</DataField>
          <DataField title="Totaal">
            <span className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(totalAmount, 2, {
              decimalSeperator: ',',
            })}`}</span>
          </DataField>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={createQuotationSchema}
          onSubmit={async (values) => {
            try {
              const { type, reference, notes, quotationDate, expiresAt, lines } = values;
              await updateQuotation(quotation.id, {
                type: type.key as QuotationType,
                reference,
                notes,
                quotationDate,
                expiresAt,
                lines: lines.map((l) => {
                  return {
                    departurePostalCode: l.departurePostalCode,
                    departureCountry: l.departureCountry.key as Country,
                    departureCity: l.departureCity,
                    arrivalPostalCode: l.arrivalPostalCode,
                    arrivalCountry: l.arrivalCountry.key as Country,
                    arrivalCity: l.arrivalCity,
                    distance: parseNumberInput(l.distance, 2),
                    price: parseNumberInput(l.price, 2),
                    trailerTypes: l.trailerTypes,
                  };
                }),
              });
              await mutate();
              trackEvent('quotation_updated', {
                quotation_id: quotation.id,
                line_count: lines.length,
              });
              toast.success('Offerte aangepast');
            } catch (err: any) {
              captureException(err);
              toast.error('Kon offerte niet aanpassen: ' + getDisplayError(err));
            }
          }}
        >
          {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <div className="mb-8">
                <Card title="Algemene informatie">
                  <SimpleComboboxField items={QUOTATION_TYPE_OPTIONS} name="type" labelText="Offerte type" />

                  <InputField type="text" labelText="Referentie klant" name="reference" isDisabled={isSubmitting} />

                  <div className="grid grid-cols-2 gap-4">
                    <InputField labelText="Offertedatum" type="date" name="quotationDate" />
                    <InputField labelText="Vervalt op" type="date" name="expiresAt" />
                  </div>

                  <TextAreaField labelText="Notities" name="notes" isDisabled={isSubmitting} spellCheck={true} />
                </Card>
              </div>

              <FieldArray
                name="lines"
                render={(arrayHelpers) => (
                  <div className="my-4">
                    <div className="flex justify-between items-end mb-4">
                      <h2 className="heading-two">Lijnen</h2>
                      <Button
                        onTrigger={() => {
                          const itemToAdd = { ...initialLineValues };
                          arrayHelpers.push(itemToAdd);
                          setLines((prev) => {
                            return [
                              ...prev,
                              {
                                departure: undefined,
                                arrival: undefined,
                                fetchKey: Date.now().toString(),
                              },
                            ];
                          });
                        }}
                        iconLeft={<PlusIcon className="button-icon" />}
                      >
                        Voeg lijn toe
                      </Button>
                    </div>

                    {values['lines'].length > 0 ? (
                      <div className="grid gap-4">
                        {values['lines'].map((_line, idx) => {
                          const fetchRoute = async (state: ILineState) => {
                            if (!state.departure || !state.arrival) {
                              return;
                            }

                            const fetchKey = `${serializeCoordinates(state.departure)}-${serializeCoordinates(
                              state.arrival,
                            )}`;
                            if (state.fetchKey === fetchKey) {
                              return;
                            }

                            setLines((prev) => {
                              prev[idx].fetchKey = fetchKey;
                              return prev;
                            });

                            const response = await createRoute(state.departure, state.arrival);
                            if (response) {
                              setLines((prev) => {
                                prev.forEach((l, idx) => {
                                  if (l.fetchKey === fetchKey) {
                                    setFieldValue(`lines[${idx}].distance`, (response.distance / 1000).toFixed(2));
                                  }
                                });

                                return prev;
                              });
                            }
                          };

                          return (
                            <div className="card p-4" key={`quotation-line-${idx}`}>
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="heading-three">Lijn {idx + 1}</h3>
                                <div>
                                  <Button
                                    color="danger"
                                    onTrigger={() => {
                                      arrayHelpers.remove(idx);
                                      setLines((prev) => {
                                        return prev.filter((_, i) => i !== idx);
                                      });
                                    }}
                                    iconLeft={<TrashIcon className="button-icon" />}
                                  >
                                    Verwijder
                                  </Button>
                                </div>
                              </div>

                              <div className="flex w-full gap-4">
                                <div className="grid quotation-line-form-columns gap-4 w-full">
                                  <div>
                                    <div className="font-medium">Van</div>
                                    <div>
                                      <AutocompletePostalcode
                                        postalCodeName={`lines[${idx}].departurePostalCode`}
                                        countryName={`lines[${idx}].departureCountry`}
                                        cityName={`lines[${idx}].departureCity`}
                                        onAutocomplete={(_postalCode, _city, longitude, latitude) => {
                                          setLines((prev) => {
                                            prev[idx].departure = [longitude, latitude];
                                            fetchRoute(prev[idx]).catch(console.error);
                                            return [...prev];
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <div className="font-medium">Naar</div>
                                    <div>
                                      <AutocompletePostalcode
                                        postalCodeName={`lines[${idx}].arrivalPostalCode`}
                                        countryName={`lines[${idx}].arrivalCountry`}
                                        cityName={`lines[${idx}].arrivalCity`}
                                        onAutocomplete={(_postalCode, _city, longitude, latitude) => {
                                          setLines((prev) => {
                                            prev[idx].arrival = [longitude, latitude];
                                            fetchRoute(prev[idx]).catch(console.error);
                                            return [...prev];
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div className="2xl:pt-6">
                                    <InputField
                                      labelText="Afstand (in km)"
                                      type="number"
                                      name={`lines[${idx}].distance`}
                                      isDisabled={isSubmitting}
                                    />
                                  </div>
                                  <div className="2xl:pt-6">
                                    <InputField
                                      labelText="Prijs"
                                      type="number"
                                      name={`lines[${idx}].price`}
                                      isDisabled={isSubmitting}
                                      step="0.01"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div className="max-w-6xl">
                                  <TrailerTypes
                                    value={values.lines[idx].trailerTypes}
                                    onChange={(newTrailerTypes) => {
                                      setFieldValue(`lines[${idx}].trailerTypes`, newTrailerTypes);
                                    }}
                                    isMultiSelect={true}
                                    isRequired={true}
                                    variant="small"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div>Geen lijnen</div>
                    )}
                  </div>
                )}
              />

              <div className="mt-8">
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  iconLeft={<PlusIcon className="button-icon" />}
                >
                  Pas offerte aan
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};
